/**
 * Pure helper for page.tsx's agentJoinedChipsByMessageId claim effect (the
 * WAITING/COLLECTING → CONNECTED transition superseding the backend's plain
 * "{name} joined the chat" SYSTEM row with HandoffStatusChip's green
 * treatment — see message-block.tsx's agentJoinedChip prop).
 *
 * AGENT_JOINED (the phase transition) and the SYSTEM_MESSAGE frame carrying
 * that copy are two independent WS sends with NO ordering guarantee between
 * them — unlike HANDOFF_ENDED, whose SYSTEM_MESSAGE the backend sends
 * synchronously in the same code path just before the phase event (see
 * page.tsx's handoffRecapsByMessageId claim effect, which can safely assume
 * "whatever's last" for exactly that reason). Naively claiming "whatever's
 * last right now" for AGENT_JOINED can therefore hijack an unrelated,
 * PRE-EXISTING system row (e.g. the WAITING queue notice) that simply
 * happens to still be last at the instant the phase flips — silently
 * deleting that row's real text and replacing it with the "joined" chip.
 *
 * The fix: only ever claim a SYSTEM row that is NEW since `sinceIndex` (the
 * transcript length as of the last render where this handoff was NOT yet
 * CONNECTED — see page.tsx's effect for how that's tracked). A pre-existing
 * row can never be at or past that index, so it can never be hijacked; if
 * the real "joined" row hasn't landed yet, this returns undefined and the
 * caller's effect just re-runs (via its `messages` dependency) once it does.
 */
import { MessageRole, type Message } from '@/types/chat';

export function resolveAgentJoinedRow(
  messages: readonly Message[],
  sinceIndex: number
): Message | undefined {
  return messages.slice(sinceIndex).find((m) => m.role === MessageRole.SYSTEM);
}
